import type { PlanWeekDatapoint } from "../../state/api.types";
import { getPlanDotColor } from "../../utilities/analyticsHelpers";
import { formatNumber } from "../../utilities/currency";
import { formatDateRange } from "../../utilities/dateHelpers";

type ChartTooltipProps = {
  active?: boolean;
  payload?: readonly { payload: PlanWeekDatapoint }[];
  planNameColorOrder: string[];
  focusedItem?: string | null;
};

export function ChartTooltip({
  active,
  payload,
  planNameColorOrder,
  focusedItem,
}: ChartTooltipProps) {
  const dataPoint = active && payload?.[0]?.payload ? payload[0].payload : null;
  if (!dataPoint) return null;

  const {
    week_start: weekStart,
    week_end: weekEnd,
    plan_counts: planCounts,
  } = dataPoint;
  const sortedPlanNames = planNameColorOrder.toSorted(
    (a, b) => (planCounts[b] ?? 0) - (planCounts[a] ?? 0),
  );
  const total = planNameColorOrder.reduce(
    (sum, planName) => sum + (planCounts[planName] ?? 0),
    0,
  );

  return (
    <div className="rounded-lg border border-secondary bg-primary p-[18px] shadow-lg">
      <p className="mb-3.5 font-mono text-[10px] leading-[10px] font-semibold text-secondary">
        {formatDateRange(weekStart, weekEnd)}
      </p>
      <div className="flex flex-col">
        {sortedPlanNames.map((planName) => {
          const backgroundColor = getPlanDotColor(planName, planNameColorOrder);
          const isFocused = focusedItem === planName || focusedItem === null;
          const value = planCounts[planName] ?? 0;

          return (
            <div
              key={planName}
              className={`mb-2.5 flex items-center justify-between gap-2.5 transition-opacity ${
                isFocused ? "opacity-100" : "opacity-30"
              }`}
            >
              <div className="flex items-center gap-2.5">
                <div
                  className="size-2 rounded-full"
                  style={{
                    backgroundColor,
                  }}
                />
                <span
                  className={`font-mono text-[10px] leading-[10px] ${
                    isFocused ? "font-semibold" : "font-normal"
                  } text-secondary`}
                >
                  {planName}
                </span>
              </div>
              <span
                className={`font-mono text-[10px] leading-[10px] ${
                  isFocused ? "font-semibold" : "font-normal"
                } text-secondary`}
              >
                {formatNumber(value)}
              </span>
            </div>
          );
        })}
        <div className="flex items-center justify-between gap-2.5">
          <span className="font-mono text-[10px] leading-[10px] font-semibold text-secondary">
            Total
          </span>
          <span className="font-mono text-[10px] leading-[10px] font-semibold text-secondary">
            {formatNumber(total)}
          </span>
        </div>
      </div>
    </div>
  );
}
